import { useTranslation } from 'react-i18next';
import {
  AutoBalanceTransfer,
  GetColorStatus,
  SupportedCurrencies,
  formattedCurrency,
  getCurrencySign,
  getMaskedNumber,
  withStyles,
} from 'vopay';
import { BodyText, Button, Card, DetailRowsCard, MaterialSymbol, Title, TransactionStatus } from 'UI';
import useTransferDetailRows from '../useTransferDetailRows';
import styles from './TransferOverview.module.scss';

const cn = withStyles(styles);

type TransferOverviewProps = {
  transfer: AutoBalanceTransfer;
  currency: SupportedCurrencies;
  onEditClick?: () => void;
  onCancelClick?: () => void;
};

export default function TransferOverview({ transfer, currency, onEditClick, onCancelClick }: TransferOverviewProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AutoBalanceTransfer.TransferOverview' });
  const { frequencyLabel, transferDetailRows, bankAccountDetailRows } = useTransferDetailRows();

  const details = { ...transfer, Currency: currency };

  const payoutReferences = [
    transfer.FinancialInstitutionNumber,
    transfer.BranchTransitNumber,
    transfer.ABARoutingNumber,
    transfer.AccountNumber && getMaskedNumber(transfer.AccountNumber, '••••'),
    currency,
  ].filter(Boolean);

  return (
    <div className={cn('overview')} data-testid="auto-balance-transfer-overview">
      <Card hoverEffect={false} className={cn('card', 'summaryCard')}>
        <div className={cn('header')}>
          <Title level="2">
            {t('summaryTitle', {
              defaultValue: '{{frequency}} Balance Transfer',
              frequency: frequencyLabel(transfer.NameOfFrequency),
            })}
          </Title>

          <TransactionStatus status={GetColorStatus('active')} label={t('active', { defaultValue: 'Active' })} />
        </div>

        <div className={cn('columns')}>
          <div className={cn('facts')}>
            <div className={cn('fact')}>
              <BodyText className={cn('muted')}>
                {t('minimumBalance', { defaultValue: 'Minimum Balance to Trigger Transfer' })}
              </BodyText>

              <Title level="4">{`${getCurrencySign(currency)}${formattedCurrency(transfer.AutoBalanceTransferAmount)}`}</Title>
            </div>

            <div className={cn('fact')}>
              <BodyText className={cn('muted')}>{t('startDate', { defaultValue: 'Start Date' })}</BodyText>
              <BodyText weight="md">{transfer.ScheduleStartDate}</BodyText>
            </div>
          </div>

          <div className={cn('payee')}>
            <BodyText size="sm" weight="em" className={cn('payeeHeading', 'muted')}>
              {t('transfersTo', { defaultValue: 'Transfers To' })}
            </BodyText>

            <div className={cn('payeeAccount')}>
              <div className={cn('payeeIcon')}>
                <MaterialSymbol icon="account_balance" size="22px" color="var(--vopayBlue)" />
              </div>

              <div>
                <BodyText weight="em">{transfer.AccountHolderName}</BodyText>

                <BodyText size="sm" className={cn('muted')}>
                  {payoutReferences.join(' · ')}
                </BodyText>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {onEditClick && (
        <Button
          id="auto-balance-transfer-edit"
          colorScheme="secondary"
          iconLeft="edit"
          className={cn('edit')}
          onClick={onEditClick}
        >
          {t('editTransferButton', { defaultValue: 'Edit Transfer' })}
        </Button>
      )}

      <DetailRowsCard
        className={cn('card')}
        title={t('transferDetails', { defaultValue: 'Transfer Details' })}
        details={transferDetailRows(details)}
      />

      <DetailRowsCard
        className={cn('card')}
        title={t('bankAccountDetails', { defaultValue: 'Bank Account Details' })}
        details={bankAccountDetailRows(details)}
      />

      {onCancelClick && (
        <Button
          id="auto-balance-transfer-cancel"
          colorScheme="destructive"
          className={cn('cancel')}
          onClick={onCancelClick}
        >
          {t('cancelTransferButton', { defaultValue: 'Cancel Transfer' })}
        </Button>
      )}
    </div>
  );
}
